import React from 'react';
import Icon from '../../../components/AppIcon';

const InspectionQueue = ({ checkpoints, selectedCheckpoint, onSelectCheckpoint }) => {
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'var(--color-error)';
      case 'high':
        return 'var(--color-warning)';
      case 'medium':
        return 'var(--color-accent)';
      case 'low':
        return 'var(--color-success)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'critical':
        return 'AlertOctagon';
      case 'high':
        return 'AlertTriangle';
      case 'medium':
        return 'AlertCircle';
      case 'low':
        return 'Info';
      default:
        return 'Circle';
    }
  };

  const formatDueDate = (date) => {
    const now = new Date();
    const due = new Date(date);
    const diffHours = Math.floor((due - now) / (1000 * 60 * 60));
    
    if (diffHours < 0) return 'Overdue';
    if (diffHours < 1) return 'Due now';
    if (diffHours < 24) return `${diffHours}h remaining`;
    return `${Math.floor(diffHours / 24)}d remaining`;
  };

  const groupedCheckpoints = checkpoints?.reduce((acc, checkpoint) => {
    const priority = checkpoint?.priority;
    if (!acc?.[priority]) acc[priority] = [];
    acc?.[checkpoint?.priority]?.push(checkpoint);
    return acc;
  }, {});

  const priorityOrder = ['critical', 'high', 'medium', 'low'];

  return (
    <div className="h-full flex flex-col bg-card">
      <div className="px-4 md:px-6 py-4 md:py-6 border-b border-border">
        <h2 className="text-lg md:text-xl font-semibold text-foreground">Inspection Queue</h2>
        <p className="caption text-muted-foreground mt-1">
          {checkpoints?.filter(c => c?.status === 'pending')?.length} pending checkpoints
        </p>
      </div>
      <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4">
        {priorityOrder?.map(priority => {
          const items = groupedCheckpoints?.[priority] || [];
          if (items?.length === 0) return null;

          return (
            <div key={priority} className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Icon 
                  name={getPriorityIcon(priority)} 
                  size={16} 
                  color={getPriorityColor(priority)} 
                />
                <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                  {priority} Priority
                </h3>
                <span className="caption text-muted-foreground">({items?.length})</span>
              </div>
              <div className="space-y-2">
                {items?.map(checkpoint => (
                  <button
                    key={checkpoint?.id}
                    onClick={() => onSelectCheckpoint(checkpoint)}
                    className={`w-full text-left p-3 md:p-4 rounded-xl border transition-all duration-250 ${
                      selectedCheckpoint?.id === checkpoint?.id
                        ? 'border-primary bg-primary/5' :'border-border bg-card hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h4 className="text-sm font-medium text-foreground line-clamp-1">
                        {checkpoint?.productName}
                      </h4>
                      <span className="data-text text-xs text-muted-foreground whitespace-nowrap">
                        #{checkpoint?.batchNumber}
                      </span>
                    </div>

                    <p className="caption text-muted-foreground line-clamp-2 mb-2">
                      {checkpoint?.checkpointType}
                    </p>

                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1">
                        <Icon name="Clock" size={12} color="var(--color-muted-foreground)" />
                        <span className="caption text-muted-foreground">
                          {formatDueDate(checkpoint?.dueDate)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Icon name="MapPin" size={12} color="var(--color-muted-foreground)" />
                        <span className="caption text-muted-foreground">
                          {checkpoint?.location}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InspectionQueue;